<template>
  <div id="CampPaymentReport">
    <div class="headerbg">
      <b-container>
        <h1>{{ title }}</h1>
      </b-container>
    </div>
    <b-container>
      <div v-if="loading" class="text-center py-5">
        <b-spinner></b-spinner>
      </div>
      <div v-else-if="loadError" class="text-center py-5">
        <p class="text-muted">{{ loadError }}</p>
        <b-button variant="primary" @click="goOverview">返回訂單總覽</b-button>
      </div>

      <template v-else-if="payment">
        <!-- 訂單資訊 -->
        <b-row class="mb-3">
          <b-col cols="12">
            <b-card>
              <b-row class="d-flex flex-wrap p-0 align-items-center mb-2">
                <b-col class="font-weight-bold">訂單編號：{{ orderNo || '-' }}</b-col>
                <b-col cols="auto">
                  <b-badge :variant="statusVariant">{{ payment.Status }}</b-badge>
                </b-col>
                <b-col cols="12"><hr class="mt-2 mb-0" /></b-col>
              </b-row>
              <b-row class="p-0 mb-2" v-if="seriesNames">
                <b-col cols="12">
                  <small class="text-muted">系列名稱</small>
                  <div class="font-weight-bold">{{ seriesNames }}</div>
                </b-col>
              </b-row>
              <b-row class="d-flex flex-wrap p-0">
                <b-col cols="5" class="font-weight-bold text-nowrap">營隊梯次</b-col>
                <b-col cols="3" class="font-weight-bold text-nowrap">學員姓名</b-col>
                <b-col cols="4" class="font-weight-bold text-nowrap text-right">金額</b-col>
                <b-col cols="12"><hr /></b-col>
              </b-row>
              <b-row
                v-for="(d, i) in payment.Details"
                :key="i"
                class="d-flex flex-wrap p-0 align-items-center"
              >
                <b-col cols="5">
                  <div>{{ d.CampProductTitle || '項目' }}</div>
                  <div class="small text-muted">
                    <span v-if="d.CampSeriesTitle">{{ d.CampSeriesTitle }} · </span>{{ d.CampProgramTitle || d.CampProgramName }}
                  </div>
                </b-col>
                <b-col cols="3">{{ d.RelationName || '-' }}</b-col>
                <b-col cols="4" class="text-right">$ {{ d.Amount | money(0, 2) }}</b-col>
                <b-col cols="12" v-if="i + 1 !== payment.Details.length"><hr /></b-col>
              </b-row>
              <b-col cols="12" style="height:2px;background:#000" class="my-3"></b-col>
              <b-col cols="12" class="text-right">
                <div class="font-weight-bold h5 mb-0">
                  應付總額：$ {{ payment.Amount | money(0, 2) }}
                </div>
              </b-col>
            </b-card>
          </b-col>
        </b-row>

        <!-- 匯款資訊與匯款回報 -->
        <b-row class="mb-3">
          <b-col cols="12">
            <b-card>
              <!-- 現金結帳：不顯示匯款資訊與回報表單，改顯示到場繳費提示 -->
              <template v-if="isCounter">
                <div class="camp-form-section-title">現金繳費</div>
                <b-alert v-if="canReport" show variant="info">
                  此訂單採「現金結帳」，請於指定時間內至現場繳費，應付金額 $ {{ payment.Amount | money(0, 2) }}。完成繳費後由後台確認收款，無須線上回報或上傳付款截圖。
                </b-alert>
                <template v-else>
                  <b-alert show :variant="statusVariant">
                    此付款單狀態為「{{ payment.Status }}」，僅供檢視。
                  </b-alert>
                  <b-row class="p-0 mt-1" v-if="payment.RejectedReason">
                    <b-col cols="6" md="3" class="text-danger">對帳失敗原因</b-col>
                    <b-col cols="6" md="9" class="text-danger">{{ payment.RejectedReason }}</b-col>
                  </b-row>
                </template>
                <div class="d-flex justify-content-end mt-3">
                  <b-button variant="primary" @click="goOverview">返回訂單總覽</b-button>
                </div>
              </template>

              <!-- 轉帳付款：維持匯款資訊與匯款回報流程 -->
              <template v-else>
              <!-- 匯款資訊 -->
              <template v-if="bankInfo">
                <div class="camp-form-section-title">匯款資訊</div>
                <b-row class="p-0">
                  <b-col cols="12" sm="3" class="text-muted">銀行</b-col>
                  <b-col cols="12" sm="9">{{ bankName || '-' }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1" v-if="bankInfo.accountName">
                  <b-col cols="12" sm="3" class="text-muted">戶名</b-col>
                  <b-col cols="12" sm="9">{{ bankInfo.accountName }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1" v-if="bankInfo.accountNo">
                  <b-col cols="12" sm="3" class="text-muted">帳號</b-col>
                  <b-col cols="12" sm="9">
                    <span class="camp-account-tag">
                      <span class="camp-account-no">{{ bankInfo.accountNo }}</span>
                      <button
                        type="button"
                        class="camp-account-copy"
                        @click="copyAccountNo"
                        v-b-tooltip.hover
                        title="複製帳號"
                      >
                        <b-icon icon="clipboard"></b-icon>
                      </button>
                    </span>
                  </b-col>
                </b-row>
                <div v-if="bankInfo.paymentInfoHtml" class="small text-muted mt-2" v-html="bankInfo.paymentInfoHtml"></div>
                <hr />
              </template>

              <!-- 可編輯 -->
              <template v-if="canReport">
                <div class="camp-form-section-title">回報匯款結果</div>
                <p class="small text-muted mb-2">標示 <span class="text-danger">*</span> 為必填項目</p>
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <template #label>付款日期 <span class="text-danger">*</span></template>
                      <b-form-input type="date" v-model="form.PaidDate" :min="minPaidDate"></b-form-input>
                      <b-form-text v-if="minPaidDate" class="text-muted">付款日期不得早於訂單成立日期（{{ minPaidDate }}）</b-form-text>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <template #label>帳號後五碼 <span class="text-danger">*</span></template>
                      <b-form-input v-model="form.AccountLast5" maxlength="5" inputmode="numeric"></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group>
                      <template #label>付款人戶名 <span class="text-danger">*</span></template>
                      <b-form-input v-model="form.PayerName"></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label="備註">
                      <b-form-input v-model="form.Memo"></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group label="付款截圖" description="選填，可協助加速對帳">
                      <b-form-file
                        v-model="proofFile"
                        accept="image/jpeg,image/png,image/webp"
                        :disabled="proofUploading"
                        browse-text="選擇檔案"
                        placeholder="未選擇檔案"
                        @input="onProofSelected"
                      ></b-form-file>
                      <div v-if="proofUploading" class="small text-muted mt-1">
                        <b-spinner small></b-spinner> 上傳中...
                      </div>
                      <div v-else-if="proofUploaded" class="mt-2 d-flex align-items-center">
                        <b-img v-if="proofPreviewUrl" :src="proofPreviewUrl" thumbnail width="72" class="mr-2"></b-img>
                        <span class="small mr-2">{{ proofFileName }}</span>
                        <b-button size="sm" variant="link" class="p-0" @click="clearProof">更換檔案</b-button>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="d-flex justify-content-end mt-2">
                  <b-button variant="outline-secondary" class="mr-2" @click="goOverview">稍後再回報</b-button>
                  <b-button variant="primary" :disabled="submitting || proofUploading" @click="submitReport">
                    {{ submitting ? '送出中...' : '送出匯款回報' }}
                  </b-button>
                </div>
              </template>

              <!-- 唯讀 -->
              <template v-else>
                <div class="camp-form-section-title">匯款回報資料</div>
                <b-alert show variant="info">
                  此付款單狀態為「{{ payment.Status }}」，僅供檢視，無法再次回報或上傳付款截圖。
                </b-alert>
                <b-row class="p-0">
                  <b-col cols="6" md="3" class="text-muted">付款日期</b-col>
                  <b-col cols="6" md="9">{{ payment.PaidDate ? $options.filters.datefmt(payment.PaidDate, 'YYYY-MM-DD') : '-' }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1">
                  <b-col cols="6" md="3" class="text-muted">帳號後五碼</b-col>
                  <b-col cols="6" md="9">{{ payment.AccountLast5 || '-' }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1">
                  <b-col cols="6" md="3" class="text-muted">付款人戶名</b-col>
                  <b-col cols="6" md="9">{{ payment.PayerName || '-' }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1">
                  <b-col cols="6" md="3" class="text-muted">備註</b-col>
                  <b-col cols="6" md="9">{{ payment.Memo || '-' }}</b-col>
                </b-row>
                <b-row class="p-0 mt-1" v-if="payment.RejectedReason">
                  <b-col cols="6" md="3" class="text-danger">對帳失敗原因</b-col>
                  <b-col cols="6" md="9" class="text-danger">{{ payment.RejectedReason }}</b-col>
                </b-row>
                <div v-if="payment.HasProofImage" class="mt-2">
                  <b-button size="sm" variant="link" class="p-0" @click="viewProof">查看付款截圖</b-button>
                </div>
                <div class="d-flex justify-content-end mt-3">
                  <b-button variant="primary" @click="goOverview">返回訂單總覽</b-button>
                </div>
              </template>
              </template>
            </b-card>
          </b-col>
        </b-row>
      </template>
    </b-container>

    <!-- 付款截圖檢視 Modal -->
    <b-modal id="modal-proof-view" title="付款截圖" size="lg" hide-footer @hidden="resetProofView">
      <div class="text-center">
        <b-img v-if="proofViewUrl" :src="proofViewUrl" fluid></b-img>
        <div v-else class="text-muted py-4">讀取中...</div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import {
  apiCampPaymentGet,
  apiCampPaymentReport,
  apiCampPaymentProofUpload,
  apiCampPaymentProofImage,
  apiGetInformation,
} from '@/api/API.js';

const PENDING_REPORT = 10;

export default {
  name: 'CampPaymentReport',
  data() {
    return {
      title: '回報匯款結果',
      paymentId: this.$route.params.paymentId,
      payment: null,
      loading: false,
      loadError: '',
      bankInfo: null,
      submitting: false,
      form: {
        PaidDate: '',
        AccountLast5: '',
        PayerName: '',
        Memo: '',
      },
      proofFile: null,
      proofUploading: false,
      proofUploaded: false,
      proofFileName: null,
      proofPreviewUrl: null,
      proofViewUrl: null,
    };
  },
  computed: {
    canReport() {
      return this.payment && this.payment.StatusNum === PENDING_REPORT;
    },
    // 現金結帳（PaymentMethod=1）不走線上回報流程。
    isCounter() {
      return this.payment && this.payment.PaymentMethodNum === 1;
    },
    bankName() {
      if (!this.bankInfo) return '';
      return [this.bankInfo.bankName, this.bankInfo.bankBranch].filter(Boolean).join(' ');
    },
    orderNo() {
      const d = this.payment && this.payment.Details && this.payment.Details[0];
      return d ? d.OrderNo : '';
    },
    seriesNames() {
      const names = ((this.payment && this.payment.Details) || [])
        .map((d) => d.CampSeriesTitle)
        .filter(Boolean);
      return [...new Set(names)].join('、');
    },
    minPaidDate() {
      const d = this.payment && this.payment.OrderCreatedAt;
      return d ? this.$options.filters.datefmt(d, 'YYYY-MM-DD') : '';
    },
    statusVariant() {
      const s = this.payment && this.payment.Status;
      if (s === '對帳中') return 'info';
      if (s === '已付款') return 'success';
      if (s === '對帳失敗') return 'danger';
      if (s === '已取消') return 'secondary';
      return 'warning';
    },
  },
  mounted() {
    if (this.IsNull(this._sessionData('TokenID')) == '') {
      alert('尚未登入');
      this._go('/');
      return;
    }
    this.loadPayment();
    this.loadBankInfo();
  },
  beforeDestroy() {
    this.clearProof();
    this.resetProofView();
  },
  methods: {
    goOverview() {
      this._go('/campOrder');
    },
    loadPayment() {
      this.loading = true;
      this.loadError = '';
      apiCampPaymentGet(this.paymentId)
        .then((res) => {
          this.payment = res.data;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.loadError = err && err.response && err.response.status === 404
            ? '找不到這筆付款紀錄，或它不屬於你的帳號'
            : '讀取付款資料失敗，請稍後再試';
        });
    },
    loadBankInfo() {
      const types = ['camp_bank_name', 'camp_bank_branch', 'camp_account_name', 'camp_account_no', 'camp_payment_info'];
      Promise.all(types.map((t) => apiGetInformation(t).catch(() => null))).then(
        ([name, branch, accName, accNo, info]) => {
          this.bankInfo = {
            bankName: name && name.data ? name.data.Content : '',
            bankBranch: branch && branch.data ? branch.data.Content : '',
            accountName: accName && accName.data ? accName.data.Content : '',
            accountNo: accNo && accNo.data ? accNo.data.Content : '',
            paymentInfoHtml: info && info.data ? info.data.Content : '',
          };
        },
      );
    },
    copyAccountNo() {
      const no = this.bankInfo && this.bankInfo.accountNo;
      if (!no) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(no).then(() => {
          this.$bvToast.toast('已複製匯款帳號', { variant: 'success', noCloseButton: true, autoHideDelay: 1500 });
        }).catch(() => alert(no));
      } else {
        alert(no);
      }
    },
    onProofSelected(file) {
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        alert('付款截圖僅接受 jpg、png、webp 格式');
        this.clearProof();
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('付款截圖大小不可超過 5MB');
        this.clearProof();
        return;
      }
      const fd = new FormData();
      fd.append('file', file);
      this.proofUploading = true;
      apiCampPaymentProofUpload(this.paymentId, fd)
        .then(() => {
          this.proofUploading = false;
          this.proofUploaded = true;
          this.proofFileName = file.name;
          if (this.proofPreviewUrl) URL.revokeObjectURL(this.proofPreviewUrl);
          this.proofPreviewUrl = URL.createObjectURL(file);
        })
        .catch(() => {
          this.proofUploading = false;
          this.clearProof();
        });
    },
    clearProof() {
      this.proofFile = null;
      this.proofUploaded = false;
      this.proofFileName = null;
      if (this.proofPreviewUrl) {
        URL.revokeObjectURL(this.proofPreviewUrl);
        this.proofPreviewUrl = null;
      }
    },
    viewProof() {
      this.proofViewUrl = null;
      this.$bvModal.show('modal-proof-view');
      apiCampPaymentProofImage(this.paymentId)
        .then((res) => {
          this.proofViewUrl = URL.createObjectURL(res.data);
        })
        .catch(() => {
          this.$bvModal.hide('modal-proof-view');
        });
    },
    resetProofView() {
      if (this.proofViewUrl) {
        URL.revokeObjectURL(this.proofViewUrl);
        this.proofViewUrl = null;
      }
    },
    submitReport() {
      if (!this.form.PaidDate || !this.form.AccountLast5 || !this.form.PayerName) {
        alert('請填寫所有必填欄位');
        return;
      }
      if (this.minPaidDate && this.form.PaidDate < this.minPaidDate) {
        alert('付款日期不得早於訂單成立日期（' + this.minPaidDate + '）');
        return;
      }
      const accountLast5 = String(this.form.AccountLast5 || '').trim();
      if (!/^\d{5}$/.test(accountLast5)) {
        alert('帳號後五碼請填寫 5 位數字');
        return;
      }
      this.submitting = true;
      apiCampPaymentReport(this.paymentId, {
        PaidDate: this.form.PaidDate,
        AccountLast5: accountLast5,
        PayerName: this.form.PayerName,
        Memo: this.form.Memo,
      })
        .then(() => {
          this.submitting = false;
          this.$bvToast.toast('匯款回報已送出，等待後台核對', { variant: 'success', noCloseButton: true, autoHideDelay: 2000 });
          this.goOverview();
        })
        .catch(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
<style scoped>
.camp-form-section-title {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.camp-account-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 12px;
  border: 1px solid #78b7df;
  border-radius: 4px;
  background: #eef8ff;
  color: #1d5f8a;
  max-width: 100%;
}

.camp-account-no {
  font-weight: 700;
  letter-spacing: 0.04em;
  word-break: break-all;
}

.camp-account-copy {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #1d5f8a;
}

.camp-account-copy:hover {
  background: #d8edf9;
}
</style>
